// src/lib/xlsxExport.ts
//
// Client-side Excel (.xlsx) export shared by the analytics + financials tabs.
// Mirrors the signature of exportCsv (src/lib/csvExport.ts) so a tab can reuse
// the exact same column array for both the CSV and Excel buttons. Builds a
// single-sheet workbook with the `xlsx` package (already a dependency, used by
// the production module) and triggers a browser download.

import * as XLSX from 'xlsx';
import type { CsvColumns } from './csvExport';

/**
 * Export `rows` to an .xlsx download. `cols` is the same shape exportCsv takes:
 * a list of [header, rowFn] tuples. Values are written with their native type
 * where possible — numbers stay numeric so Excel can sum/format them, and
 * null/undefined become blank cells.
 */
export function exportXlsx<T>(filename: string, cols: CsvColumns<T>, rows: T[]) {
  const header = cols.map((c) => c[0]);
  const body = rows.map((r) =>
    cols.map((c) => {
      const v = c[1](r);
      return v == null ? '' : v;
    }),
  );
  const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
